import axios from 'axios';
import { isValidToken, getRefreshToken, setSession, clearSession, identifyInPostHogFromAccess } from './jwt';

// ----------------------------------------------------------------------

const getBaseUrl = () => {
  const { host, protocol } = window.location;
  return `${protocol}//${host}`;
};

const getApiBaseUrl = () => {
  // Frontend runs on 3001 in dev, backend on 8001
  return getBaseUrl().replace(/:\d+/, ':8001');
};

const axiosInstance = axios.create({
  baseURL: `${getApiBaseUrl()}/api/v1`,
  timeout: 250000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

// Refresh queue: prevents parallel refresh attempts from thundering the backend
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Request interceptor — inject Bearer token on every call
axiosInstance.interceptors.request.use(
  (config) => {
    const access = localStorage.getItem('access');
    if (access && isValidToken(access)) {
      config.headers.Authorization = `Bearer ${access}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — on 401, try refresh once, requeue the original request
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Rechazar con el error COMPLETO de axios (no solo response.data):
    // los consumidores leen e.response.status / e.response.data para
    // distinguir 402/404/409; con data-only esas ramas eran inalcanzables.
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Never try to refresh on the refresh or obtain endpoints themselves
    if (originalRequest.url?.includes('/token/')) {
      return Promise.reject(error);
    }

    // A refresh is already in flight — park this request until it finishes
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      isRefreshing = false;
      clearSession();
      return Promise.reject(error);
    }

    try {
      const response = await axios.post(`${getApiBaseUrl()}/api/v1/token/refresh/`, {
        refresh: refreshToken,
      });
      const { access } = response.data;
      setSession(access);
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${access}`;
      identifyInPostHogFromAccess(access);
      processQueue(null, access);
      originalRequest.headers.Authorization = `Bearer ${access}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearSession();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Legacy helper shims — preserve BearerConfig contract so all AxiosCall /
// AxiosPost / AxiosFile callers keep working without changes.
const BearerConfig = (headers = []) => {
  const access = localStorage.getItem('access');
  if (access && isValidToken(access)) {
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${access}`;
    if (headers.length > 0) {
      headers.forEach((h) => {
        axiosInstance.defaults.headers[h.head] = h.value;
      });
    }
  } else {
    localStorage.removeItem('access');
    delete axiosInstance.defaults.headers.common.Authorization;
  }
};

export async function AxiosCall(url) {
  BearerConfig();
  return axiosInstance.get(url);
}

export async function AxiosPost(url, data) {
  BearerConfig();
  return axiosInstance.post(url, data);
}

export async function AxiosFile(url, data) {
  BearerConfig([{ head: 'Content-Type', value: 'multipart/form-data' }]);
  return axiosInstance.post(url, data);
}

export default axiosInstance;
